import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, Collapse, IconButton, InputAdornment, MenuItem, Select, Skeleton, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { analyticsApi } from '../services/api';
import { ErrorState } from '../components/common/States';
import EmptyState from '../components/common/EmptyState';
import ComingSoon from '../components/common/ComingSoon';
import SubTabs from '../components/common/SubTabs';
import InfoTooltip from '../components/common/InfoTooltip';
import { apiError, formatDate, formatDateShort, formatMoney, formatNumber, formatPercent, formatRelativeMsk, pluralize } from '../utils/format';
import { hasData, mpPath } from '../utils/marketplaces';
import { tokens } from '../theme';

const RISK = {
  loss: { label: 'Уйдёт в минус', color: tokens.error, bg: tokens.errorSoft },
  low: { label: 'Низкая маржа', color: tokens.warning, bg: tokens.warningSoft },
  ok: { label: 'Выгодно', color: tokens.success, bg: tokens.successSoft },
  unknown: { label: 'Нет данных', color: tokens.inkSoft, bg: tokens.bg },
};

const MISSING = {
  no_sales: 'нет продаж за 30 дней — комиссию и логистику не из чего посчитать',
  no_cost: 'не указана себестоимость',
  no_commission: 'не удалось определить комиссию',
};

const mskDate = (ts) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Moscow' }).format(new Date(ts));

const RiskChip = ({ risk }) => {
  const r = RISK[risk] || RISK.unknown;
  return <Chip size="small" label={r.label} sx={{ bgcolor: r.bg, color: r.color, fontWeight: 600, border: risk === 'unknown' ? `1px solid ${tokens.border}` : 'none' }} />;
};

const Money = ({ v, strong, color }) => (
  <Typography component="span" sx={{ fontWeight: strong ? 700 : 500, color: color || (v != null && Number(v) < 0 ? tokens.error : tokens.ink), whiteSpace: 'nowrap' }}>
    {formatMoney(v)}
  </Typography>
);

// Breakdown of one unit at one price, for the «!» tooltip.
const breakdown = (u, taxStatus) => [
  `Цена: ${formatMoney(u.price, { kopecks: true })}`,
  `− комиссия WB: ${formatMoney(u.commission, { kopecks: true })}`,
  `− логистика на 1 шт: ${formatMoney(u.logistics, { kopecks: true })}`,
  `− хранение на 1 шт: ${formatMoney(u.storage, { kopecks: true })}`,
  `− себестоимость: ${formatMoney(u.cost_price, { kopecks: true })}`,
  taxStatus === 'computed' ? `− налог: ${formatMoney(u.tax, { kopecks: true })}` : '− налог: не задан, прибыль до налога',
  `= прибыль: ${formatMoney(u.profit, { kopecks: true })}`,
].join('\n');

const ProfitCell = ({ u, taxStatus }) => (
  <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
    <Box sx={{ textAlign: 'right' }}>
      <Money v={u.profit} strong />
      <Typography variant="caption" color="text.secondary" component="div">{u.margin != null ? `маржа ${formatPercent(u.margin)}` : ' '}</Typography>
    </Box>
    {u.profit != null && <InfoTooltip exclamation size={15}>{breakdown(u, taxStatus)}</InfoTooltip>}
  </Stack>
);

const ItemsTable = ({ items, taxStatus }) => (
  <TableContainer sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
    <Table size="small" sx={{ minWidth: 980, '& td, & th': { borderColor: tokens.border } }}>
      <TableHead>
        <TableRow sx={{ '& th': { color: tokens.inkSoft, fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', bgcolor: tokens.primaryTint } }}>
          <TableCell>Товар</TableCell>
          <TableCell>Участие</TableCell>
          <TableCell align="right">Цена сейчас → в акции</TableCell>
          <TableCell align="right">Прибыль с 1 шт сейчас</TableCell>
          <TableCell align="right">Прибыль с 1 шт в акции</TableCell>
          <TableCell align="right">Разница</TableCell>
          <TableCell>Риск</TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
              <span>Рост продаж</span>
              <InfoTooltip>Во сколько раз должны вырасти продажи в акции, чтобы общая прибыль не стала меньше, чем сейчас: прибыль с 1 шт сейчас ÷ прибыль с 1 шт в акции.</InfoTooltip>
            </Stack>
          </TableCell>
          <TableCell align="right">Остаток</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((it) => {
          const off = it.now.price > 0 ? Math.round((1 - it.promo.price / it.now.price) * 100) : null;
          return (
            <TableRow key={`${it.account_id}-${it.nm_id}`} hover>
              <TableCell sx={{ maxWidth: 300 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar variant="rounded" src={it.photo_url || undefined} alt="" sx={{ width: 32, height: 42, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
                    <ImageOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{it.title || 'Без названия'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{it.vendor_code || '—'} · WB {it.nm_id}</Typography>
                  </Box>
                </Stack>
              </TableCell>
              <TableCell>
                {it.in_action
                  ? <Chip size="small" label="в акции" sx={{ bgcolor: tokens.primarySoft, color: tokens.primaryDark, fontWeight: 600 }} />
                  : <Typography variant="body2" color="text.secondary" noWrap>не участвует</Typography>}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body2" component="span" color="text.secondary">{formatMoney(it.now.price)} → </Typography>
                <Money v={it.promo.price} strong />
                {off != null && off > 0 && <Typography variant="caption" color="text.secondary" component="div">−{off}% к текущей цене</Typography>}
              </TableCell>
              <TableCell align="right"><ProfitCell u={it.now} taxStatus={taxStatus} /></TableCell>
              <TableCell align="right"><ProfitCell u={it.promo} taxStatus={taxStatus} /></TableCell>
              <TableCell align="right"><Money v={it.profit_change} /></TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <RiskChip risk={it.risk} />
                  {it.missing.length > 0 && <InfoTooltip exclamation size={15}>{it.missing.map((m) => MISSING[m] || m).join('\n')}</InfoTooltip>}
                </Stack>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {it.sales_growth != null
                  ? <Typography variant="body2" sx={{ fontWeight: 600 }}>×{String(it.sales_growth).replace('.', ',')}</Typography>
                  : <Tooltip title={it.risk === 'loss' ? 'В акции товар убыточен: рост продаж прибыль не вернёт' : 'Нельзя посчитать'}><Typography variant="body2" color="text.secondary">—</Typography></Tooltip>}
              </TableCell>
              <TableCell align="right">{it.stock != null ? `${formatNumber(it.stock)} шт.` : '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const ProductCell = ({ it }) => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Avatar variant="rounded" src={it.photo_url || undefined} alt="" sx={{ width: 32, height: 42, bgcolor: tokens.primaryTint, color: tokens.inkMuted }}>
      <ImageOutlinedIcon fontSize="small" />
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{it.title || 'Без названия'}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap component="div">{it.vendor_code || '—'} · WB {it.nm_id}</Typography>
    </Box>
  </Stack>
);

// WB does not give the discount of an auto promotion through the API: the seller looks it up in
// the cabinet and types it in, and every forecast article is recomputed at that discount the same
// way the backend's UnitAt does (kopecks, commission and tax rounded per unit).
const WB_PROMO_URL = (id) => `https://seller.wildberries.ru/dp-promo-calendar?action=${encodeURIComponent(id)}`;
const kop = (v) => (v == null ? null : Math.round(Number(v) * 100));
const unitAtDiscount = (it, f, disc) => {
  const price = Math.round(kop(it.now.price) * (100 - disc) / 100);
  const fixed = [it.now.logistics, it.now.storage, it.now.cost_price].map(kop);
  if (it.commission_pct == null || fixed.some((x) => x == null)) return { price: price / 100, profit: null };
  const commission = Math.round(price * it.commission_pct / 100);
  let tax = 0;
  if (f.tax_rate != null) {
    const base = f.tax_on_payout ? price - commission : price;
    tax = base > 0 ? Math.round(base * f.tax_rate / 100) : 0;
  }
  return { price: price / 100, profit: (price - commission - fixed[0] - fixed[1] - fixed[2] - tax) / 100 };
};
const scenarioCounts = (f, disc) => {
  let loss = 0; let ok = 0;
  f.items.forEach((it) => {
    const u = unitAtDiscount(it, f, disc);
    if (u.profit == null) return;
    if (u.profit < 0) loss++; else ok++;
  });
  return { loss, ok };
};

const DISCOUNT_KEY = 'marqit.autoPromoDiscounts';
const discountKey = (p) => `${p.account_id}:${p.id}`;
const loadDiscounts = () => {
  try { return JSON.parse(localStorage.getItem(DISCOUNT_KEY)) || {}; } catch (e) { return {}; }
};
const saveDiscounts = (d) => {
  try { localStorage.setItem(DISCOUNT_KEY, JSON.stringify(d)); } catch (e) { /* private mode */ }
};

// «Посмотреть процент на WB» + the field the seller types the auto promotion discount into.
const DiscountInput = ({ promo, value, onChange }) => {
  const [text, setText] = useState(value != null ? String(value).replace('.', ',') : '');
  useEffect(() => { setText(value != null ? String(value).replace('.', ',') : ''); }, [value]);
  const commit = (t) => {
    setText(t);
    const n = Number(t.replace(',', '.'));
    if (t.trim() === '') onChange(null);
    else if (Number.isFinite(n) && n > 0 && n < 100) onChange(Math.round(n * 10) / 10);
  };
  const bad = text.trim() !== '' && !(Number(text.replace(',', '.')) > 0 && Number(text.replace(',', '.')) < 100);
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
      <TextField size="small" label="Скидка автоакции" value={text} onChange={(e) => commit(e.target.value)} error={bad}
        helperText={bad ? 'Число от 1 до 99' : undefined} inputProps={{ inputMode: 'decimal', 'aria-label': 'Скидка автоакции, %' }}
        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} sx={{ width: { sm: 190 } }} />
      <Button size="small" variant="outlined" href={WB_PROMO_URL(promo.id)} target="_blank" rel="noopener noreferrer" sx={{ whiteSpace: 'nowrap' }}>
        Посмотреть процент на WB
      </Button>
      <Typography variant="caption" color="text.secondary">
        Откройте акцию в кабинете WB, посмотрите процент скидки и введите его здесь: Marqit посчитает прибыль каждого товара, если он попадёт в акцию.
      </Typography>
    </Stack>
  );
};

// Forecast of an auto promotion: goods WB is likely to take and how much discount each can bear,
// and the profit at the discount the seller typed in.
const ForecastTable = ({ forecast, taxStatus, discount }) => (
  <TableContainer sx={{ border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
    <Table size="small" sx={{ minWidth: 860, '& td, & th': { borderColor: tokens.border } }}>
      <TableHead>
        <TableRow sx={{ '& th': { color: tokens.inkSoft, fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap', bgcolor: tokens.primaryTint } }}>
          <TableCell>Товар</TableCell>
          <TableCell align="right">Продаж в день</TableCell>
          <TableCell align="right">Средняя цена продажи</TableCell>
          <TableCell align="right">Прибыль с 1 шт сейчас</TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
              <span>Цена без убытка</span>
              <InfoTooltip>Самая низкая цена, при которой товар ещё не уходит в минус: комиссия WB, логистика, хранение, себестоимость и налог покрыты.</InfoTooltip>
            </Stack>
          </TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
              <span>Выдержит скидку</span>
              <InfoTooltip>{`Насколько можно снизить цену от средней цены продажи до цены без убытка. Меньше ${forecast.safe_discount}% — товар под угрозой в автоакции.`}</InfoTooltip>
            </Stack>
          </TableCell>
          {discount != null && <TableCell align="right">{`Цена со скидкой ${formatPercent(discount)}`}</TableCell>}
          {discount != null && <TableCell align="right">{`Прибыль с 1 шт при ${formatPercent(discount)}`}</TableCell>}
          <TableCell align="right">Остаток</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {forecast.items.map((it) => {
          const sc = discount != null ? unitAtDiscount(it, forecast, discount) : null;
          const bad = sc ? sc.profit != null && sc.profit < 0 : it.risky;
          return (
          <TableRow key={it.nm_id} hover sx={bad ? { bgcolor: tokens.errorSoft } : undefined}>
            <TableCell sx={{ maxWidth: 300 }}><ProductCell it={it} /></TableCell>
            <TableCell align="right">{it.sold_per_day != null ? String(it.sold_per_day).replace('.', ',') : '—'}</TableCell>
            <TableCell align="right"><Money v={it.now.price} /></TableCell>
            <TableCell align="right"><ProfitCell u={it.now} taxStatus={taxStatus} /></TableCell>
            <TableCell align="right">{it.break_even != null ? <Money v={it.break_even} strong /> : '—'}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {it.max_discount != null ? (
                <Typography variant="body2" sx={{ fontWeight: 700, color: it.risky ? tokens.error : tokens.success }}>до {formatPercent(it.max_discount)}</Typography>
              ) : it.risky ? (
                <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.error }}>уже в минусе</Typography>
              ) : (
                <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                  <Typography variant="body2" color="text.secondary">нет данных</Typography>
                  {it.missing.length > 0 && <InfoTooltip exclamation size={15}>{it.missing.map((m) => MISSING[m] || m).join('\n')}</InfoTooltip>}
                </Stack>
              )}
            </TableCell>
            {sc && <TableCell align="right"><Money v={sc.price} /></TableCell>}
            {sc && (
              <TableCell align="right">
                {sc.profit != null ? <Money v={sc.profit} strong color={sc.profit < 0 ? tokens.error : tokens.success} /> : <Typography variant="body2" color="text.secondary">нет данных</Typography>}
              </TableCell>
            )}
            <TableCell align="right">{it.stock != null ? `${formatNumber(it.stock)} шт.` : '—'}</TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const daysUntil = (ts) => Math.ceil((new Date(ts).getTime() - Date.now()) / 86400000);
const whenText = (promo) => {
  if (promo.status === 'current') return 'уже идёт';
  const d = daysUntil(promo.start);
  return d <= 1 ? 'начнётся завтра' : `начнётся через ${d} ${pluralize(d, 'день', 'дня', 'дней')}`;
};
// Auto promotions running or starting within a week need the seller's attention: «danger» when
// goods may bring a loss (by the cabinet prices when read, else at the discount the seller typed
// in, else by the forecast), «check» when the discount was not typed in yet.
const AUTO_ALERT_DAYS = 7;
const cabinetLoss = (p) => p.items.length > 0 ? p.summary.loss + p.summary.low : null;
const autoRisky = (p, disc) => cabinetLoss(p)
  ?? (!p.forecast ? 0 : disc != null ? scenarioCounts(p.forecast, disc).loss : p.forecast.risky);
const autoAttention = (p, disc) => {
  if (p.type !== 'auto' || (p.status !== 'current' && daysUntil(p.start) > AUTO_ALERT_DAYS)) return null;
  if (autoRisky(p, disc) > 0) return 'danger';
  return p.items.length === 0 && disc == null && p.forecast && p.forecast.candidates > 0 ? 'check' : null;
};

const SummaryChip = ({ n, label, risk }) => (n > 0 ? (
  <Chip size="small" label={`${label}: ${n}`} sx={{ bgcolor: RISK[risk].bg, color: RISK[risk].color, fontWeight: 600 }} />
) : null);

const PromoCard = ({ promo, taxStatus, accountName, defaultOpen, discount, onDiscount }) => {
  const [open, setOpen] = useState(defaultOpen);
  const s = promo.summary;
  const auto = promo.type === 'auto';
  const f = auto ? promo.forecast : null;
  const fromCabinet = auto && promo.items.length > 0; // exact goods and prices read from the WB cabinet
  const expandable = promo.items.length > 0 || (!!f && f.candidates > 0);
  const attention = autoAttention(promo, discount);
  const sc = f && discount != null ? scenarioCounts(f, discount) : null;
  const wbCounts = promo.in_action_total != null && promo.not_in_action_total != null;
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}
          onClick={() => expandable && setOpen(!open)} sx={{ cursor: expandable ? 'pointer' : 'default' }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" component="h3">{promo.name || `Акция ${promo.id}`}</Typography>
              {auto && <Chip size="small" label="Автоакция" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {formatDateShort(mskDate(promo.start))} — {formatDate(mskDate(promo.end))}
              {accountName ? ` · ${accountName}` : ''}
              {promo.participation_percentage != null ? ` · участвует ${promo.participation_percentage}% товаров с остатком` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {auto && !fromCabinet ? (
              !f || f.candidates === 0 ? (
                <Typography variant="body2" color="text.secondary">Нет товаров с продажами и остатком, которые WB мог бы взять</Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    WB, скорее всего, возьмёт {f.candidates} {pluralize(f.candidates, 'товар', 'товара', 'товаров')}
                  </Typography>
                  {sc ? (
                    <>
                      {sc.loss > 0 && <Chip size="small" label={`в минус при ${formatPercent(discount)}: ${sc.loss}`} sx={{ bgcolor: RISK.loss.bg, color: RISK.loss.color, fontWeight: 600 }} />}
                      {sc.ok > 0 && <Chip size="small" label={`в плюсе: ${sc.ok}`} sx={{ bgcolor: RISK.ok.bg, color: RISK.ok.color, fontWeight: 600 }} />}
                    </>
                  ) : (
                    <>
                      {f.risky > 0 && <Chip size="small" label={`под угрозой: ${f.risky}`} sx={{ bgcolor: RISK.loss.bg, color: RISK.loss.color, fontWeight: 600 }} />}
                      {f.candidates - f.risky - f.unknown > 0 && <Chip size="small" label={`с запасом: ${f.candidates - f.risky - f.unknown}`} sx={{ bgcolor: RISK.ok.bg, color: RISK.ok.color, fontWeight: 600 }} />}
                    </>
                  )}
                  {f.unknown > 0 && <SummaryChip n={f.unknown} label="нет данных" risk="unknown" />}
                  <IconButton size="small" aria-label={open ? 'Свернуть' : 'Показать товары'} aria-expanded={open}
                    sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                    <ExpandMoreRoundedIcon />
                  </IconButton>
                </>
              )
            ) : !promo.items_loaded && !fromCabinet ? (
              <Typography variant="body2" color="text.secondary">Товары загружаются…</Typography>
            ) : s.items === 0 ? (
              <Typography variant="body2" color="text.secondary">WB не предложил товаров</Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                  {s.items} {pluralize(s.items, 'товар', 'товара', 'товаров')}, в акции {s.in_action}
                </Typography>
                <SummaryChip n={s.loss} label="в минус" risk="loss" />
                <SummaryChip n={s.low} label="низкая маржа" risk="low" />
                <SummaryChip n={s.ok} label="выгодно" risk="ok" />
                <SummaryChip n={s.unknown} label="нет данных" risk="unknown" />
                <IconButton size="small" aria-label={open ? 'Свернуть' : 'Показать товары'} aria-expanded={open}
                  sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </>
            )}
          </Stack>
        </Stack>
        {promo.advantages.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {promo.advantages.map((a) => <Chip key={a} size="small" label={a} sx={{ bgcolor: tokens.primaryTint, color: tokens.inkSoft }} />)}
          </Stack>
        )}
        {auto && (fromCabinet || (f && f.candidates > 0)) && (
          <Alert severity={attention === 'danger' ? 'error' : attention === 'check' ? 'warning' : 'info'} sx={{ mt: 1.5 }}>
            {fromCabinet ? (
              <>
                Цены акции из вашего кабинета WB, получены {formatRelativeMsk(promo.cabinet_read_at)}.{' '}
                {cabinetLoss(promo) > 0
                  ? `${cabinetLoss(promo)} ${pluralize(cabinetLoss(promo), 'товар', 'товара', 'товаров')} в акции уйдёт в минус или с низкой маржой — исключите их в кабинете WB («Настроить список товаров»), если не готовы работать в минус ради продвижения.`
                  : 'Все товары по цене акции в плюсе.'}
              </>
            ) : (
              <>
                {f.unknown === f.candidates
                  ? `Автоакция ${whenText(promo)}. Прибыль не посчитать: у товаров нет себестоимости или продаж за 30 дней — укажите себестоимость в разделе «Себестоимость и налоги». `
                  : sc
                  ? (sc.loss > 0
                    ? `Если WB возьмёт товары со скидкой ${formatPercent(discount)}, ${sc.loss} ${pluralize(sc.loss, 'товар уйдёт', 'товара уйдут', 'товаров уйдут')} в минус — исключите ${sc.loss === 1 ? 'его' : 'их'} в кабинете WB («Настроить список товаров»), если не готовы работать в минус ради продвижения. `
                    : `При скидке ${formatPercent(discount)} все товары с данными остаются в плюсе. `)
                  : f.risky > 0
                  ? `Автоакция ${whenText(promo)}. ${f.risky} ${pluralize(f.risky, 'товар выдержит', 'товара выдержат', 'товаров выдержат')} скидку меньше ${formatPercent(f.safe_discount)} или уже ${pluralize(f.risky, 'продаётся', 'продаются', 'продаются')} в минус — в акции ${f.risky === 1 ? 'он может' : 'они могут'} принести убыток. `
                  : `Автоакция ${whenText(promo)}. Все товары с данными выдержат скидку от ${formatPercent(f.safe_discount)}. `}
                Это прогноз: считаем, что в акцию попадут все товары с продажами за 30 дней.{sc ? '' : ' Введите процент скидки акции ниже, чтобы увидеть прибыль по каждому товару.'}
              </>
            )}
            {wbCounts ? ` По данным WB сейчас в акции ${promo.in_action_total} ${pluralize(promo.in_action_total, 'товар', 'товара', 'товаров')}, не участвует ${promo.not_in_action_total}.` : ''}
          </Alert>
        )}
        {auto && !fromCabinet && f && f.candidates > 0 && <DiscountInput promo={promo} value={discount} onChange={onDiscount} />}
        <Collapse in={open} unmountOnExit>
          <Box sx={{ mt: 2 }}>
            {auto && !fromCabinet ? <ForecastTable forecast={f} taxStatus={taxStatus} discount={discount} /> : <ItemsTable items={promo.items} taxStatus={taxStatus} />}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const PromotionsPage = () => {
  const { marketplace, accounts } = useOutletContext();
  const [accountId, setAccountId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('current');
  const [discounts, setDiscounts] = useState(loadDiscounts);
  const setDiscount = (p, v) => setDiscounts((d) => {
    const next = { ...d };
    if (v == null) delete next[discountKey(p)]; else next[discountKey(p)] = v;
    saveDiscounts(next);
    return next;
  });
  const discOf = (p) => discounts[discountKey(p)] ?? null;

  // only the latest request may fill the page: an answer for a previous cabinet is dropped
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!hasData(marketplace)) return;
    const seq = ++loadSeq.current;
    setError('');
    try {
      const { data: d } = await analyticsApi.promotions(accountId ? { account_id: accountId } : {});
      if (seq === loadSeq.current) setData(d);
    } catch (e) {
      if (seq === loadSeq.current) setError(apiError(e));
    }
  }, [marketplace, accountId]);

  useEffect(() => { setData(null); load(); }, [load]);

  const groups = useMemo(() => {
    const list = data?.promotions || [];
    return { current: list.filter((p) => p.status === 'current'), upcoming: list.filter((p) => p.status === 'upcoming') };
  }, [data]);

  if (!hasData(marketplace)) return <ComingSoon marketplace={marketplace} what="Акции" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const names = Object.fromEntries((data?.sources || []).map((s) => [s.account_id, s.account_name]));
  const many = (data?.sources || []).length > 1;
  const noAccess = (data?.sources || []).filter((s) => s.status === 'no_access');
  const loading = (data?.sources || []).filter((s) => ['pending', 'running'].includes(s.status) && !s.last_success_at);
  const failed = (data?.sources || []).filter((s) => s.status === 'error');
  const shown = groups[tab];
  const urgent = (data?.promotions || []).filter((p) => autoAttention(p, discOf(p)));
  const toCheck = urgent.filter((p) => autoAttention(p, discOf(p)) === 'check');

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'flex-start' }} sx={{ mb: 2.5 }}>
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h3">Аналитика акций</Typography>
          <Typography variant="body2" color="text.secondary">
            Текущие и ближайшие акции WB: прибыль с каждого товара по цене акции и риск уйти в минус. Комиссия, логистика и хранение —
            средние по товару из финансовых отчётов {data ? `с ${formatDate(data.stats_from)} по ${formatDate(data.stats_to)}` : 'за последние 30 дней'}.
          </Typography>
        </Box>
        {accounts.length > 1 && (
          <Select size="small" value={accountId || 'all'} onChange={(e) => setAccountId(e.target.value === 'all' ? '' : e.target.value)} inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 220 }}>
            <MenuItem value="all">Все кабинеты</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
          </Select>
        )}
      </Stack>

      <Stack spacing={1.5} sx={{ mb: 2.5 }}>
        {urgent.filter((p) => autoAttention(p, discOf(p)) === 'danger').map((p) => {
          const disc = discOf(p);
          const risky = autoRisky(p, disc);
          return (
            <Alert key={`urgent-${p.account_id}-${p.id}`} severity="error" variant="filled"
              action={<Button color="inherit" size="small" href={WB_PROMO_URL(p.id)} target="_blank" rel="noopener noreferrer" sx={{ whiteSpace: 'nowrap' }}>Открыть на WB</Button>}>
              {many ? `«${names[p.account_id]}»: ` : ''}автоакция «{p.name}» {whenText(p)}: {risky} {pluralize(risky, 'товар', 'товара', 'товаров')} под угрозой убытка
              {disc != null && cabinetLoss(p) == null ? ` при скидке ${formatPercent(disc)}` : ''}.
              Исключите убыточные товары в кабинете WB.
            </Alert>
          );
        })}
        {toCheck.length > 0 && (
          <Alert severity="warning">
            {toCheck.length === 1
              ? `Автоакция «${toCheck[0].name}» ${whenText(toCheck[0])}.`
              : `${toCheck.length} ${pluralize(toCheck.length, 'автоакция идёт', 'автоакции идут', 'автоакций идут')} или скоро ${toCheck.length === 1 ? 'начнётся' : 'начнутся'}.`}
            {' '}Посмотрите процент скидки на WB и введите его в карточке акции: Marqit посчитает прибыль по каждому товару.
          </Alert>
        )}
        {noAccess.map((s) => (
          <Alert key={s.account_id} severity="warning"
            action={<Button component={RouterLink} to={mpPath(marketplace, 'connections')} color="inherit" size="small">Подключения</Button>}>
            {many ? `«${s.account_name}»: ` : ''}у токена нет доступа к календарю акций. Создайте в кабинете WB персональный токен с категорией
            «Цены и скидки» (или отметьте все категории) и обновите его в подключении.
          </Alert>
        ))}
        {failed.map((s) => (
          <Alert key={s.account_id} severity="error">
            {many ? `«${s.account_name}»: ` : ''}не удалось загрузить календарь акций{s.last_error ? `: ${s.last_error}` : ''}. Загрузка повторится автоматически.
          </Alert>
        ))}
        {loading.map((s) => (
          <Alert key={s.account_id} severity="info">{many ? `«${s.account_name}»: ` : ''}календарь акций загружается — обычно это занимает несколько минут.</Alert>
        ))}
        {data && data.tax_status !== 'computed' && (
          <Alert severity="info" action={<Button component={RouterLink} to={mpPath(marketplace, 'costs')} color="inherit" size="small">Налоги</Button>}>
            {data.tax_status === 'not_supported'
              ? 'Для вашего налогового режима налог в акциях пока не считается — прибыль показана до налога.'
              : 'Налог не задан — прибыль показана до налога. Укажите ставку в разделе «Себестоимость и налоги».'}
          </Alert>
        )}
      </Stack>

      {!data ? (
        <Stack spacing={2}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: '16px' }} />)}</Stack>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <SubTabs value={tab} onChange={setTab} items={[
              { key: 'current', label: `Идут сейчас · ${groups.current.length}` },
              { key: 'upcoming', label: `Ближайшие · ${groups.upcoming.length}` },
            ]} />
            <Typography variant="caption" color="text.secondary">
              Риск: «в минус» — цена акции не покрывает расходы; «низкая маржа» — меньше {formatPercent(data.low_margin)} от цены.
              {data.sources.some((s) => s.last_success_at) ? ` Обновлено ${formatRelativeMsk(data.sources.map((s) => s.last_success_at).filter(Boolean).sort().pop())}.` : ''}
            </Typography>
          </Stack>
          {shown.length === 0 ? (
            <Card><EmptyState icon={<LocalOfferOutlinedIcon fontSize="inherit" />}
              title={tab === 'current' ? 'Сейчас акций нет' : 'Ближайших акций нет'}
              description={loading.length || noAccess.length ? 'Список появится, когда календарь акций загрузится.' : 'WB пока не опубликовал акции на этот срок.'} /></Card>
          ) : (
            <Stack spacing={1.5}>
              {shown.map((p, i) => (
                <PromoCard key={`${p.account_id}-${p.id}`} promo={p} taxStatus={data.tax_status} accountName={many ? names[p.account_id] : ''}
                  defaultOpen={i === 0 && p.items.length > 0} discount={discOf(p)} onDiscount={(v) => setDiscount(p, v)} />
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default PromotionsPage;
